import React, { useCallback, useEffect, useMemo, useState } from "react";
import BreadCrumb from "Common/BreadCrumb";
import moment from "moment";

// icons
import {
  Search,
  Plus,
  Trash2,
  FileEdit,
  ChevronRight,
  ChevronLeft,
  Loader2,
} from "lucide-react";
import { Link } from "react-router-dom";
import TableContainer from "Common/TableContainer";

// react-redux
import { useDispatch, useSelector } from "react-redux";
import { createSelector } from "reselect";

// Formik
import * as Yup from "yup";
import { useFormik } from "formik";

import {
  getJewelleryDiscount as onGetJewelleryDiscount,
} from "slices/thunk";
import { ToastContainer } from "react-toastify";
import filterDataBySearch from "Common/filterDataBySearch";
import {
  setSelectedProducts,
  resetSelectedProducts,
  updateDiscountInfo,
  setDiscountInfo,
} from "slices/discount/reducer";
import DiscountModel from "./Component/DiscountModel";
import { useTabContext } from "context/TabContext";

/* eslint-disable react-hooks/exhaustive-deps */
const Discount = () => {
  const dispatch = useDispatch<any>();
  const { changeTab } = useTabContext();

  const selectDataList = createSelector(
    (state: any) => state.Discount,
    (state) => ({
      discounts: state.discounts,
      selectedProducts: state.selectedProducts,
      pagination: state.pagination,
      isLoading: state.isLoading,
    })
  );

  const { discounts, selectedProducts, pagination, isLoading } =
    useSelector(selectDataList);

  const [data, setData] = useState<any>([]);
  const [eventData, setEventData] = useState<any>();
  const [show, setShow] = useState<boolean>(false);
  const [isEdit, setIsEdit] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [showAllProducts, setShowAllProducts] = useState<boolean>(false);

  // Delete Modal
  // const deleteToggle = () => setDeleteModal(!deleteModal);

  // Delete Data
  const onClickDelete = (cell: any) => {
    // setDeleteModal(true);
    if (cell._id) {
      setEventData(cell);
    }
  };

  const validation: any = useFormik({
    // enableReinitialize : use this flag when initial values needs to be changed
    enableReinitialize: true,
    initialValues: {
      type: (eventData && eventData.type) || "",
      value: (eventData && eventData.value) || "",
      startDate: (eventData && eventData.startDate) || "",
      endDate: (eventData && eventData.endDate) || "",
    },
    validationSchema: Yup.object({
      type: Yup.string().required("Please Enter Type"),
      value: Yup.string()
        .matches(/^\d+$/, "Value must be a valid number") // Allows only digits
        .required("Please Enter Value"),
      // value: Yup.number().required("Please Enter value"),
      startDate: Yup.date()
        .min(
          moment().startOf("day").toDate(),
          "Start date cannot be in the past"
        )
        .required("Please Select date"),
      endDate: Yup.date().required("Please Select date"),
    }),

    onSubmit: (values: any) => {
      // save new Discount
      if (isEdit) {
        dispatch(
          updateDiscountInfo({
            id: eventData._id,
            fields: {
              type: values.type,
              startDate: moment(values.startDate).toISOString(),
              endDate: moment(values.endDate).toISOString(),
              value: Number(values.value),
            },
          })
        );

        // const updateData = {
        //     id: eventData._id,
        //     fields: {
        //         type: values.type,
        //         startDate: moment(values.startDate).toISOString(),
        //         endDate: moment(values.endDate).toISOString(),
        //         value: Number(values.value),
        //     },
        // };
      } else {
        dispatch(
          setDiscountInfo({
            type: values.type,
            value: Number(values.value),
            startDate: moment(values.startDate).toISOString(),
            endDate: moment(values.endDate).toISOString(),
          })
        );
      }
      changeTab("discountJewelleryProduct");
      // toggle();
    },
  });

  // const handleDelete = () => {
  //   if (eventData) {
  //     dispatch(onDeleteJewelleryMetal(eventData._id));
  //     setDeleteModal(false);
  //   }
  // };
  //

  // Update Data
  const handleUpdateDataClick = (ele: any) => {
    setEventData({ ...ele });
    dispatch(setSelectedProducts(ele?.products));
    setIsEdit(true);
    setShow(true);
  };

  // const handleResetForm = () => {
  //   // You can reset the form here (passed as a prop)
  //   if (validation) {
  //     validation.resetForm();
  //   }
  // };

  //

  const toggle = useCallback(() => {
    if (show) {
      setShow(false);
      setEventData("");
      setIsEdit(false);
      validation.resetForm();
      dispatch(resetSelectedProducts());
      changeTab("discountInfo");
      setShowAllProducts(false);
    } else {
      setShow(true);
      setEventData("");
      validation.resetForm();
      dispatch(resetSelectedProducts());
      changeTab("discountInfo");
      // setShowAllProducts(false);
    }
  }, [show, validation, dispatch]);

  // Search Data
  const filterSearchData = (e: any) => {
    const search = e.target.value;
    const keysToSearch = ["_id", "type", "value"];
    filterDataBySearch(discounts, search, keysToSearch, setData);
  };

  const handleSearchChange = (e: any) => {
    setSearchQuery(e.target.value); // Update the search query
    filterSearchData(e); // Call the debounced filter function
  };

  // Get Data
  useEffect(() => {
    const payload = {
      page: currentPage,
      limit: pagination?.limit,
      search: searchQuery,
    };
    dispatch(onGetJewelleryDiscount(payload));
  }, [dispatch, currentPage, searchQuery]);

  useEffect(() => {
    setData(discounts);
  }, [discounts]);

  // useEffect(() => {
  //   if (discounts !== data) {
  //     setData(discounts);
  //   }
  // }, [discounts, data]);

  const columns = useMemo(
    () => [
      {
        header: (
          <div className="flex items-center h-full">
            <input
              id="CheckboxAll"
              className="size-4 cursor-pointer bg-white border border-slate-200 checked:bg-none dark:bg-zink-700 dark:border-zink-500 rounded-sm appearance-none arrow-none relative after:absolute after:content-['\eb7b'] after:top-0 after:left-0 after:font-remix after:leading-none after:opacity-0 checked:after:opacity-100 after:text-custom-500 checked:border-custom-500 dark:after:text-custom-500 dark:checked:border-custom-800"
              type="checkbox"
            />
          </div>
        ),
        enableSorting: false,
        id: "checkAll",
        cell: (cell: any) => {
          return (
            <div className="flex items-center h-full">
              <input
                id={"Checkbox" + cell.row.original.id}
                className="size-4 cursor-pointer bg-white border border-slate-200 checked:bg-none dark:bg-zink-700 dark:border-zink-500 rounded-sm appearance-none arrow-none relative after:absolute after:content-['\eb7b'] after:top-0 after:left-0 after:font-remix after:leading-none after:opacity-0 checked:after:opacity-100 after:text-custom-500 checked:border-custom-500 dark:after:text-custom-500 dark:checked:border-custom-800"
                type="checkbox"
              />
            </div>
          );
        },
      },
      {
        header: "Discount ID",
        accessorKey: "_id",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cell: any) => (
          <>
            <Link
              to="#!"
              className="transition-all duration-150 ease-linear order_id text-custom-500 hover:text-custom-600"
            >
              {cell.getValue()}
            </Link>
          </>
        ),
      },
      {
        header: "Type",
        accessorKey: "type",
        enableColumnFilter: false,
      },
      {
        header: "Value",
        accessorKey: "value",
        enableColumnFilter: false,
      },
      {
        header: "startDate",
        accessorKey: "startDate",
        enableColumnFilter: false,
      },
      {
        header: "endDate",
        accessorKey: "endDate",
        enableColumnFilter: false,
      },
      {
        header: "Action",
        enableColumnFilter: false,
        enableSorting: true,
        cell: (cell: any) => (
          <div className="flex items-center  gap-2">
            <a
              href="#!"
              className="flex items-center justify-center size-8 transition-all duration-150 ease-linear rounded-md bg-slate-100 hover:bg-slate-200 dark:bg-zink-600 dark:hover:bg-zink-500"
              onClick={() => {
                const data = cell.row.original;
                handleUpdateDataClick(data);
              }}
            >
              <FileEdit className="size-3"></FileEdit>
            </a>

            <a
              href="#!"
              className="flex items-center justify-center size-8 transition-all duration-150 ease-linear rounded-md bg-slate-100 hover:bg-slate-200 dark:bg-zink-600 dark:hover:bg-zink-500"
              onClick={() => {
                const data = cell.row.original;
                onClickDelete(data);
              }}
            >
              <Trash2 className="size-3"></Trash2>
            </a>
          </div>
        ),
      },
    ],
    []
  );

  const onPageChange = (page: number) => {
    setCurrentPage(page);
  };
  const totalPages = Math.ceil(pagination.total / pagination.limit);

  const getPages = () => Array.from({ length: totalPages }, (_, i) => i + 1);

  return (
    <React.Fragment>
      <BreadCrumb title="Add Discount" pageTitle="Jewellery" />
      {/* <DeleteModal
        show={deleteModal}
        onHide={deleteToggle}
        onDelete={handleDelete}
      /> */}
      <ToastContainer closeButton={false} limit={1} />

      <div className="card" id="ordersTable">
        <div className="card-body">
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-12">
            <div className="lg:col-span-3">
              <div className="relative">
                <input
                  type="text"
                  className="ltr:pl-8 rtl:pr-8 search form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                  placeholder="Search for ..."
                  autoComplete="off"
                  onChange={handleSearchChange}
                  // onChange={(e) => filterSearchData(e)}
                />
                <Search className="inline-block size-4 absolute ltr:left-2.5 rtl:right-2.5 top-2.5 text-slate-500 dark:text-zink-200 fill-slate-100 dark:fill-zink-600" />
              </div>
            </div>
            <div className="lg:col-span-2 lg:col-start-11">
              <div className="ltr:lg:text-right rtl:lg:text-left">
                <Link
                  to="#!"
                  data-modal-target="addOrderModal"
                  type="button"
                  className="text-white btn bg-custom-500 border-custom-500 hover:text-white hover:bg-custom-600 hover:border-custom-600 focus:text-white focus:bg-custom-600 focus:border-custom-600 focus:ring focus:ring-custom-100 active:text-white active:bg-custom-600 active:border-custom-600 active:ring active:ring-custom-100 dark:ring-custom-400/20"
                  onClick={toggle}
                >
                  <Plus className="inline-block size-4" />{" "}
                  <span className="align-middle">Add Discount</span>
                </Link>
              </div>
            </div>
          </div>
          {isLoading ? (
            <div className="relative flex items-center justify-center min-h-full">
              <div className="flex justify-center fixed top-[50%]">
                <Loader2 className="size-[35px] ltr:mr-2 rtl:ml-2 animate-spin" />
              </div>
            </div>
          ) : (
            <TableContainer
              isPagination={false}
              columns={columns || []}
              data={data || []}
              customPageSize={10}
              divclassName="mt-5 overflow-x-auto"
              tableclassName="w-full whitespace-nowrap"
              theadclassName="ltr:text-left rtl:text-right bg-slate-100 dark:bg-zink-600"
              thclassName="px-3.5 py-2.5 font-semibold text-slate-500 border-b border-slate-200 dark:border-zink-500 dark:text-zink-200"
              tdclassName="px-3.5 py-2.5 border-y border-slate-200 dark:border-zink-500"
              PaginationClassName="flex flex-col items-center mt-5 md:flex-row"
            />
          )}
          {!data?.length && !isLoading && (
            <div className="noresult">
              <div className="py-6 text-center">
                <Search className="size-6 mx-auto text-sky-500 fill-sky-100 dark:sky-500/20" />
                <h5 className="mt-2 mb-1">Sorry! No Result Found</h5>
                <p className="mb-0 text-slate-500 dark:text-zink-200">
                  We've searched more than 299+ orders We did not find any
                  orders for you search.
                </p>
              </div>
            </div>
          )}
          <ul className="flex justify-end pt-8 flex-wrap items-center gap-2 shrink-0">
            {/* Prev Button */}
            <li>
              <button
                onClick={() => currentPage > 1 && onPageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className={`inline-flex items-center justify-center bg-white dark:bg-zink-700 h-8 px-3 transition-all duration-150 ease-linear border rounded border-slate-200 dark:border-zink-500 ${
                  currentPage !== 1 ? "hover:text-blue-500" : "text-gray-300"
                }`}
              >
                <ChevronLeft />
              </button>
            </li>
            {/* Page Numbers */}
            {getPages().map((page) => (
              <li key={page}>
                <button
                  onClick={() => onPageChange(page)}
                  className={`inline-flex items-center justify-center bg-white dark:bg-zink-700 h-8 px-3 transition-all duration-150 ease-linear border rounded border-slate-200 dark:border-zink-500 text-slate-500 dark:text-zink-200 ${
                    currentPage === page
                      ? "bg-blue-500 text-blue-500"
                      : "hover:bg-gray-200"
                  }`}
                >
                  {page}
                </button>
              </li>
            ))}
            {/* Next Button */}
            <li>
              <button
                onClick={() =>
                  currentPage < totalPages && onPageChange(currentPage + 1)
                }
                disabled={currentPage === totalPages}
                className={`inline-flex items-center justify-center bg-white dark:bg-zink-700 h-8 px-3 transition-all duration-150 ease-linear border rounded border-slate-200 dark:border-zink-500 ${
                  currentPage !== totalPages
                    ? "hover:text-blue-500"
                    : "text-gray-300"
                }`}
              >
                <ChevronRight />
              </button>
            </li>
          </ul>
        </div>
      </div>
      <DiscountModel
        discounts={discounts}
        validation={validation}
        show={show}
        toggle={toggle}
        eventData={eventData}
        selectedProducts={selectedProducts}
        isEdit={isEdit}
        showAllProducts={showAllProducts}
        setShowAllProducts={setShowAllProducts}
      />
    </React.Fragment>
  );
};

export default Discount;
